// Manager actions for an AM: nudge (lands in their bell), assign a task,
// log an activity, plus report settings (call aliases, hide).
import { useState } from "react";
import { Send, ListPlus, Activity, Link2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import ActivityLogModal from "../../../components/activity/ActivityLogModal";

const inp = "px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/10 text-[12px] text-white outline-none focus:border-orange-400/60";

export function AMActions({ am }) {
  const [msg, setMsg] = useState("");
  const [task, setTask] = useState({ title: "", due_date: "" });
  const [logOpen, setLogOpen] = useState(false);
  const nudge = async () => {
    if (!msg.trim()) return;
    try { await api.post(`/am-reports/${am.id}/nudge`, { message: msg }); setMsg(""); toast.success(`Nudge sent to ${am.name}`); }
    catch { toast.error("Could not send nudge"); }
  };
  const assign = async () => {
    if (!task.title.trim()) return;
    try {
      await api.post("/workspace/tasks", { title: task.title.trim(), assignee_id: am.id, due_date: task.due_date || null, priority: "high" });
      setTask({ title: "", due_date: "" }); toast.success(`Task assigned to ${am.name}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not assign task"); }
  };
  return (
    <div className="grid md:grid-cols-3 gap-2" data-testid="am-actions">
      <div className="flex gap-1.5">
        <input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder={`Nudge ${am.name.split(" ")[0]}…`} className={`${inp} flex-1`} data-testid="am-nudge-input" />
        <button type="button" onClick={nudge} data-testid="am-nudge-send" className="px-2.5 rounded-lg border border-amber-500/40 text-amber-200 hover:bg-amber-500/10"><Send className="w-3.5 h-3.5" /></button>
      </div>
      <div className="flex gap-1.5">
        <input value={task.title} onChange={(e) => setTask((t) => ({ ...t, title: e.target.value }))} placeholder="Assign a task…" className={`${inp} flex-1`} data-testid="am-task-title" />
        <input type="date" value={task.due_date} onChange={(e) => setTask((t) => ({ ...t, due_date: e.target.value }))} className={`${inp} w-[130px]`} data-testid="am-task-due" />
        <button type="button" onClick={assign} data-testid="am-task-assign" className="px-2.5 rounded-lg border border-indigo-500/40 text-indigo-200 hover:bg-indigo-500/10"><ListPlus className="w-3.5 h-3.5" /></button>
      </div>
      <button type="button" onClick={() => setLogOpen(true)} data-testid="am-log-activity"
        className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-sky-500/40 text-sky-200 text-[12px] hover:bg-sky-500/10 py-1.5"><Activity className="w-3.5 h-3.5" /> Log activity for {am.name.split(" ")[0]}</button>
      {logOpen && <ActivityLogModal personId={null} defaultAssigneeName={am.name} onClose={() => setLogOpen(false)} onSaved={() => { setLogOpen(false); toast.success("Activity logged"); }} />}
    </div>
  );
}

export function AMSettings({ am, callNames, unmatched, onChanged }) {
  const [aliases, setAliases] = useState(am.call_aliases || []);
  const [add, setAdd] = useState("");
  const save = async (next) => {
    setAliases(next);
    try { await api.patch(`/am-reports/${am.id}/settings`, { call_aliases: next }); toast.success("Call aliases saved"); onChanged(); }
    catch { toast.error("Could not save"); }
  };
  return (
    <div className="space-y-3" data-testid="am-settings">
      <div className="rounded-xl border border-white/10 p-3">
        <div className="text-[11px] uppercase tracking-widest font-mono text-slate-400 mb-1 flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Call aliases</div>
        <p className="text-[11.5px] text-slate-400 mb-2">JustCall agent names linked to {am.name}. Auto-matched now: {callNames.length ? callNames.join(", ") : "none"}.</p>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {aliases.map((a) => (
            <span key={a} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-teal-500/40 text-teal-200 text-[11px] font-mono" data-testid={`am-alias-${a}`}>
              {a}<button type="button" onClick={() => save(aliases.filter((x) => x !== a))} data-testid={`am-alias-remove-${a}`}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <input value={add} onChange={(e) => setAdd(e.target.value)} list="am-unmatched-list" placeholder="Add a JustCall name…" className={inp} data-testid="am-alias-input" />
          <datalist id="am-unmatched-list">{unmatched.map((u) => <option key={u} value={u} />)}</datalist>
          <button type="button" onClick={() => { if (add.trim()) { save([...new Set([...aliases, add.trim()])]); setAdd(""); } }} data-testid="am-alias-add"
            className="px-3 py-1.5 rounded-lg border border-teal-500/40 text-teal-200 text-[12px]">Link</button>
          {unmatched.map((u) => (
            <button key={u} type="button" onClick={() => save([...new Set([...aliases, u])])} data-testid={`am-alias-quick-${u}`}
              className="px-2 py-1 rounded-full border border-amber-500/40 text-amber-200 text-[11px]">+ {u}</button>
          ))}
        </div>
      </div>
    </div>
  );
}
